/**
 * EarningsPage Component
 *
 * Main page for viewing earnings and requesting payouts.
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HostLayout } from '../components';
import {
  useEarnings,
  usePayoutMethods,
  usePayouts,
} from './hooks';
import {
  EarningsOverview,
  EarningsList,
  RequestPayoutModal,
  AddPayoutMethodModal,
  PayoutHistoryList,
} from './components';

type TabType = 'earnings' | 'payouts' | 'methods';
type EarningsFilter = 'all' | 'pending' | 'available' | 'paid_out';

const FILTER_OPTIONS: { id: EarningsFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'available', label: 'Available' },
  { id: 'pending', label: 'Pending' },
  { id: 'paid_out', label: 'Paid Out' },
];

export const EarningsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabType) || 'earnings';
  const activeFilter = (searchParams.get('filter') as EarningsFilter) || 'all';

  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showAddMethodModal, setShowAddMethodModal] = useState(false);

  const { earnings, summary, isLoading: earningsLoading } = useEarnings(
    activeFilter === 'all' ? undefined : { status: activeFilter }
  );
  const { methods, isLoading: methodsLoading, addMethod, setDefaultMethod, deleteMethod } = usePayoutMethods();
  const { payouts, isLoading: payoutsLoading, requestPayout, isRequesting } = usePayouts();

  const handleTabChange = (tab: TabType) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    if (tab === 'earnings') {
      params.delete('filter');
    }
    setSearchParams(params);
  };

  const handleFilterChange = (filter: EarningsFilter) => {
    const params = new URLSearchParams(searchParams);
    params.set('filter', filter);
    setSearchParams(params);
  };

  const handleRequestPayout = async (payoutMethodId: string) => {
    if (!summary) return;
    await requestPayout({
      amount: summary.available,
      payoutMethodId,
    });
  };

  return (
    <HostLayout title="Wallet">
      <div className="max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <p className="text-gray-500 -mt-4">Your wallet balance and payouts</p>
        {summary && summary.available >= 50 && (
          <button
            onClick={() => setShowPayoutModal(true)}
            className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
          >
            Request Payout
          </button>
        )}
      </div>

      {/* Overview */}
      {summary && (
        <EarningsOverview
          summary={summary}
          onRequestPayout={summary.available >= 50 ? () => setShowPayoutModal(true) : undefined}
        />
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b">
        <button
          onClick={() => handleTabChange('earnings')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'earnings'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Earnings History
        </button>
        <button
          onClick={() => handleTabChange('payouts')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'payouts'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Payout History
        </button>
        <button
          onClick={() => handleTabChange('methods')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'methods'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Payout Methods
        </button>
      </div>

      {/* Earnings Tab */}
      {activeTab === 'earnings' && (
        <>
          {/* Filter */}
          <div className="flex gap-2 mb-6">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => handleFilterChange(option.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === option.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <EarningsList earnings={earnings} isLoading={earningsLoading} />
        </>
      )}

      {/* Payouts Tab */}
      {activeTab === 'payouts' && (
        <PayoutHistoryList payouts={payouts} isLoading={payoutsLoading} />
      )}

      {/* Payout Methods Tab */}
      {activeTab === 'methods' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Your Payout Methods</h2>
            <button
              onClick={() => setShowAddMethodModal(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              + Add Method
            </button>
          </div>

          {methodsLoading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : methods.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <CreditCardIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No payout methods</h3>
              <p className="text-gray-500 mb-4">
                Add a payout method to receive your earnings.
              </p>
              <button
                onClick={() => setShowAddMethodModal(true)}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
              >
                Add Payout Method
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {methods.map((method) => (
                <div
                  key={method.id}
                  className="bg-white rounded-xl border border-gray-200 p-4"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      method.type === 'whish_money' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {method.type === 'whish_money' ? (
                        <PhoneIcon className="w-6 h-6 text-green-600" />
                      ) : (
                        <BankIcon className="w-6 h-6 text-blue-600" />
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900">{method.name}</h3>
                        {method.isDefault && (
                          <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {method.type === 'whish_money'
                          ? (method.details as any).phoneNumber
                          : `${(method.details as any).bankName} - ****${(method.details as any).accountNumber.slice(-4)}`}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {!method.isDefault && (
                        <button
                          onClick={() => setDefaultMethod(method.id)}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          Set as default
                        </button>
                      )}
                      <button
                        onClick={() => deleteMethod(method.id)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {summary && (
        <RequestPayoutModal
          isOpen={showPayoutModal}
          onClose={() => setShowPayoutModal(false)}
          onRequest={handleRequestPayout}
          isRequesting={isRequesting}
          availableAmount={summary.available}
          payoutMethods={methods}
        />
      )}

      <AddPayoutMethodModal
        isOpen={showAddMethodModal}
        onClose={() => setShowAddMethodModal(false)}
        onSave={addMethod}
        existingMethodsCount={methods.length}
      />
      </div>
    </HostLayout>
  );
};

// Icons
const CreditCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

const PhoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

const BankIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

export default EarningsPage;
